import { useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { channelsPath, environmentPath } from '../app/routes';
import { focusManager, focusableClassName } from '../helpers/spatialFocus';
import { getRemoteAction } from '../helpers/remoteKeys';
import { unloadSdks } from '../lib/sdk';

const repeatDelay = 110;

const sdkOwnsFocus = (): boolean => {
  const sdkElement = document.getElementById('inthegame-instance');
  return Boolean(sdkElement?.contains(document.activeElement));
};

const sdkOverlayIsActive = async (): Promise<boolean> => {
  if (!window.inthegame) return false;

  try {
    const state = await Promise.race([
      window.inthegame.getITGState(),
      new Promise<never>((_, reject) => {
        window.setTimeout(() => reject(new Error('SDK state timeout')), 500);
      }),
    ]);
    return state.isOverlayActive;
  } catch {
    return false;
  }
};

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return (
    target.isContentEditable ||
    tagName === 'input' ||
    tagName === 'select' ||
    tagName === 'textarea'
  );
};

const playerOwnsNavigation = (target: EventTarget | null): boolean => {
  const element = target instanceof Element ? target : document.activeElement;
  if (element?.closest(`.${focusableClassName}`)) return false;
  return Boolean(
    element?.closest('[data-player-root]') ||
    document.activeElement?.closest('[data-player-root]'),
  );
};

export const useRemoteNavigation = (): void => {
  const location = useLocation();
  const navigate = useNavigate();
  const backInProgress = useRef(false);
  const lastMove = useRef(0);

  // This runs during render so child effects always record focus against the
  // route that produced them, including when React mounts a route from cache.
  focusManager.setScope(location.pathname);

  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) =>
      focusManager.track(event.target);

    const handlePointerDown = (event: MouseEvent) => {
      const target = (event.target as Element | null)?.closest<HTMLElement>(
        `.${focusableClassName}`,
      );
      if (target) focusManager.focus(target);
    };

    const handleBack = async () => {
      if (backInProgress.current) return;
      backInProgress.current = true;

      try {
        if (await sdkOverlayIsActive()) return;

        const channelMatch = matchPath(
          '/:env/:accountId/:channelId',
          location.pathname,
        );
        if (channelMatch?.params.env && channelMatch.params.accountId) {
          navigate(
            `${channelsPath(
              channelMatch.params.env,
              channelMatch.params.accountId,
            )}${location.search}`,
          );
          return;
        }

        const channelsMatch = matchPath('/:env/:accountId', location.pathname);
        if (channelsMatch?.params.env) {
          unloadSdks();
          navigate(
            `${environmentPath(channelsMatch.params.env)}${location.search}`,
          );
          return;
        }

        const shouldClose = window.confirm
          ? window.confirm('Are you sure you want to close the app?')
          : true;
        if (shouldClose) window.close();
      } finally {
        backInProgress.current = false;
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const action = getRemoteAction(event);
      if (!action || event.defaultPrevented || sdkOwnsFocus()) return;

      if (action === 'back') {
        event.preventDefault();
        void handleBack();
        return;
      }

      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        isEditableTarget(event.target) ||
        playerOwnsNavigation(event.target)
      ) {
        return;
      }

      event.preventDefault();

      if (action === 'select') {
        focusManager.select();
        return;
      }

      const now = Date.now();
      if (event.repeat && now - lastMove.current < repeatDelay) return;
      lastMove.current = now;
      focusManager.move(action);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [location.pathname, location.search, navigate]);
};
